/**
 * Sagesse — Design Tokens (source of truth)
 * Extracted from the prototype (prototype/app/sagesse-a.css, Direction A "Modern Heraldry").
 * Consumed by mobile (React Native), admin + fan web (React).
 *
 * ⚠ PENDING BRAND SIGN-OFF (decisions-log D-009):
 *   - Primary green was darkened to #0A3C2E in the prototype (brand spec primary is #1A5C2A).
 *   - `accentMint` (#7FCBA8) was introduced as the interactive accent, demoting gold to honors only.
 *   Confirm both with the client; change here and everything updates.
 *
 * A11Y RULE: gold (#C9A84C) must never be small body text on white (fails WCAG AA).
 *   Use gold on dark surfaces, or as a fill behind dark ink. Helper: `color.goldOnLightAllowed = false`.
 */

export const color = {
  brand: {
    green: '#0A3C2E',       // PRIMARY — buttons, nav, headers, active
    greenDeep: '#062A20',   // hero / drench / darkest surface
    green700: '#0D4A38',
    green600: '#10573F',    // primary hover
    green400: '#2F7A62',
    green300: '#4F9A80',    // light green accent
    green200: '#86BCAA',
  },
  gold: {
    base: '#C9A84C',        // honors / tiers / verified — ACCENT, used sparingly
    deep: '#A6863A',
    soft: '#E7D29A',
    onLightAllowed: false,  // a11y: not as small text on white
  },
  accentMint: '#7FCBA8',    // ⚠ interactive accent (pending approval — see header)
  /**
   * White ramp — dark mode's SECONDARY colour (#103). Every dark surface is brand
   * green, so a green accent on it barely separates; white is the second voice that
   * does. `base` is reserved for logos/marks and the strongest accents, the alphas
   * are for secondary text, hairlines and washes over green.
   */
  white: {
    base: '#FFFFFF',                    // logos / marks / strongest accent
    strong: 'rgba(255,255,255,0.90)',   // accent text
    soft: 'rgba(255,255,255,0.70)',     // secondary accent text
    line: 'rgba(255,255,255,0.30)',     // accent hairline / rule
    wash: 'rgba(255,255,255,0.10)',     // accent fill over green
  },
  semantic: {
    positive: '#5FBF94',
    negative: '#E8796D',
    warning: '#E0B24E',
    info: '#6FB3D6',
    live: '#FF5A4D',
  },
} as const;

/**
 * Mobile uses dark-first brand surfaces and a light variant per-screen (D-007).
 *
 * The `accent*` roles are the scheme's SECONDARY voice — the colour that separates
 * from the dominant surface (#103). Dark surfaces are green, so its secondary is
 * white; light surfaces are near-white, so its secondary is brand green. Mint stays
 * the PRIMARY interactive accent in both; these are the second layer, not a
 * replacement. `brandMark` is what the club mark/wordmark is tinted to.
 */
export const theme = {
  dark: {
    appBg: '#06281E',
    surface: '#0B3729',
    surface2: '#0A3023',
    surface3: '#0F4233',           // raised
    border: 'rgba(235,243,238,0.12)',
    borderStrong: 'rgba(235,243,238,0.22)',
    text: '#EFF5F1',
    textDim: 'rgba(239,245,241,0.64)',
    textFaint: 'rgba(239,245,241,0.42)',
    accent: color.white.base,      // white — secondary accent on green
    accentSoft: color.white.soft,
    accentLine: color.white.line,
    accentWash: color.white.wash,
    onAccent: '#06281E',           // ink on a white accent fill
    brandMark: color.white.base,   // logos read pure white on green (15.8:1 on appBg)
  },
  light: {
    appBg: '#F4F7F2',
    surface: '#FFFFFF',
    surface2: '#EEF2EC',
    surface3: '#E7EDE6',
    border: 'rgba(10,42,28,0.12)',
    borderStrong: 'rgba(10,42,28,0.20)',
    text: '#10271D',
    textDim: 'rgba(16,39,29,0.60)',
    textFaint: 'rgba(16,39,29,0.40)',
    accent: color.brand.green,     // deep green — secondary accent on white
    accentSoft: 'rgba(10,60,46,0.70)',
    accentLine: 'rgba(10,60,46,0.30)',
    accentWash: 'rgba(10,60,46,0.08)',
    onAccent: color.white.base,    // ink on a green accent fill
    brandMark: color.brand.green,  // logos stay green on light surfaces
  },
} as const;

/** Admin / fan web — light product surface (from prototype/admin.html). */
export const adminPalette = {
  white: '#FCFDFB',
  offwhite: '#F4F6F2',
  divider: '#E2E6DF',
  ink: '#15241A',
  inkDim: '#5C6B60',
  red: '#C4453A',
  amber: '#C98A2C',
} as const;

/** 4-based spacing scale (px). */
export const space = {
  0: 0, 1: 2, 2: 4, 3: 6, 4: 8, 5: 10, 6: 12, 8: 16, 10: 20, 12: 24, 16: 32, 20: 40, 24: 48,
} as const;

export const radius = {
  sm: 11,
  btn: 13,
  base: 16,
  lg: 20,
  pill: 999,
} as const;

export const font = {
  family: {
    display: 'Marcellus',          // mobile display (heraldic). Admin/web use `ui` instead.
    ui: 'Hanken Grotesk',          // admin/web UI + body (D-008)
    body: 'Inter',                 // mobile body fallback
    arabic: 'Noto Kufi Arabic',    // all Arabic, RTL
  },
  /** Canonical scale (px) — use these in the real apps, not the prototype's mock-scaled sizes. */
  size: { caption: 12, bodySm: 14, body: 16, h3: 20, h2: 24, h1: 32, display: 40 },
  weight: { regular: 400, medium: 500, semibold: 600, bold: 700, heavy: 800 },
  lineHeight: { tight: 1.15, snug: 1.3, normal: 1.5 },
  tabularNums: true,               // scores, prices, timers, stats
} as const;

/**
 * Elevation — web box-shadow strings (green-tinted, layered: contact + ambient).
 * React Native: use `elevation` (Android) + shadow* props (iOS); the `rnLevel` hint maps roughly.
 */
export const elevation = {
  card:   { web: '0 1px 0 rgba(255,255,255,0.04) inset, 0 8px 22px -16px rgba(0,0,0,0.7)', rnLevel: 2 },
  raised: { web: '0 1px 0 rgba(255,255,255,0.06) inset, 0 16px 34px -20px rgba(0,0,0,0.7)', rnLevel: 6 },
  // Primary button: top highlight + hard bottom "lip" + ambient (tactile press collapses the lip).
  buttonPrimary: { web: 'inset 0 1px 0 rgba(255,255,255,0.16), 0 3px 0 #05231A, 0 9px 18px -8px rgba(4,30,20,0.78)', rnLevel: 4 },
} as const;

export const motion = {
  // ease-out only, no bounce (per the design laws). Durations in ms.
  duration: { fast: 120, base: 180, slow: 250 },
  easing: 'cubic-bezier(0.22, 1, 0.36, 1)', // ease-out-quint
  pressTranslateY: 2, // px the primary button depresses on :active
} as const;

export const tokens = { color, theme, adminPalette, space, radius, font, elevation, motion } as const;
export default tokens;
